import Checklist from '@repo/datalayer/store/models/Checklist'
import { observer } from 'mobx-react-lite'
import { ChecklistItemView } from './ChecklistItemView'
import { Button } from '@/components/ui/button'
import { useRouter } from '@/hooks/router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, CheckCircle, RefreshCw, Calendar } from 'lucide-react'

export const CompletedChecklistView = observer(function CompletedChecklistView({
  checklist,
}: {
  checklist: Checklist
}) {
  const router = useRouter()
  const { t } = useTranslation()

  const completed = checklist.items.filter((i) => i.complete).length
  const skipped = checklist.items.filter((i) => i.skipped).length

  return (
    <main className="max-w-[900px] mx-auto w-full overflow-auto relative">
      <div className="px-4 py-6">
        {/* Back button */}
        <button
          onClick={() => router.navigateToHome()}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">{t('backToHome')}</span>
        </button>

        {/* Hero Header - Green gradient for completed checklists */}
        <div className="bg-success rounded-3xl p-8 mb-6 text-white relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            {/* Completed badge */}
            <div className="relative flex items-center justify-center w-20 h-20">
              <div className="absolute inset-0 bg-white/20 rounded-full" />
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span
                  data-test-id="completed-checklist-badge"
                  className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium"
                >
                  {t('checklists.completed')}
                </span>
              </div>
              <h1
                data-test-id="completed-checklist-title"
                className="text-2xl md:text-3xl font-bold mb-2"
              >
                {checklist?.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-white/80">
                <span>
                  {completed} {t('checklists.itemsCompleted')}
                </span>
                {skipped > 0 && (
                  <span>
                    {skipped} {t('checklists.itemsSkipped')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Completion info and actions */}
          <div className="relative flex flex-wrap items-center justify-between gap-3 mt-6 pt-6 border-t border-white/20">
            <div className="flex items-center gap-2 text-white/80">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">
                {checklist.completed_at?.toLocaleString()}
              </span>
            </div>
            <Button
              onClick={async (e) => {
                e.stopPropagation()
                e.preventDefault()

                checklist.rerun().then((newChecklist) => {
                  router.navigateToChecklist(newChecklist.id)
                })
              }}
              className="bg-cream text-forest hover:bg-cream/90"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('checklists.rerun')}
            </Button>
          </div>
        </div>

        {/* Checklist Items */}
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="divide-y">
            {checklist?.items.map((item) => (
              <ChecklistItemView
                key={item.id}
                checklist={checklist}
                item={item}
              />
            ))}
          </div>
        </div>
      </div>
    </main>
  )
})
